/**
 * SqlEditorForm - SQL transformation editor with syntax highlighting
 */

import React, { useState } from 'react';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Button from '@amzn/awsui-components-console/button';
import Alert from '@amzn/awsui-components-console/alert';
import Header from '@amzn/awsui-components-console/header';
import Container from '@amzn/awsui-components-console/container';
import Table from '@amzn/awsui-components-console/table';
import ExpandableSection from '@amzn/awsui-components-console/expandable-section';
import CodeEditor, { CodeEditorProps } from '@amzn/awsui-components-console/code-editor';
import { runSql, validateSql } from '../../../../lib/duckdb-wasm';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiUrl, apiFetch, apiFetchDefaults } from '../../../../utils/apiClient';

interface SqlEditorFormProps {
  sql: string;
  onChange: (sql: string) => void;
  onSchemaUpdate?: (columns: { columnName: string; dataType: string; nullable: boolean; sampleValues: string[] }[]) => void;
  onRegenerate?: () => void;
  datasetId?: string;
  fileId?: string;
  schema?: { columnName: string; dataType: string; nullable?: boolean }[];
  filePreview?: string[][] | null;
}

/** Simple SQL formatter — newline before major keywords, indent columns */
function formatSql(raw: string): string {
  const keywords = /\b(SELECT|FROM|WHERE|AND|OR|GROUP BY|ORDER BY|HAVING|LIMIT|LEFT JOIN|RIGHT JOIN|INNER JOIN|JOIN|ON|CASE|WHEN|THEN|ELSE|END|UNION ALL|UNION)\b/gi;
  let s = raw.replace(/\s+/g, ' ').trim();
  s = s.replace(keywords, (m) => '\n' + m.toUpperCase());
  // indent after SELECT: put each column on its own line
  s = s.replace(/^SELECT\s+/i, 'SELECT\n  ');
  s = s.replace(/,\s*/g, ',\n  ');
  // indent WHERE conditions
  s = s.replace(/\nAND\b/g, '\n  AND');
  s = s.replace(/\nOR\b/g, '\n  OR');
  return s.trim();
}

const i18nStrings: CodeEditorProps.I18nStrings = {
  loadingState: 'Loading code editor',
  errorState: 'There was an error loading the code editor.',
  errorStateRecovery: 'Retry',
  editorGroupAriaLabel: 'Code editor',
  statusBarGroupAriaLabel: 'Status bar',
  cursorPosition: (row, column) => `Ln ${row}, Col ${column}`,
  errorsTab: 'Errors',
  warningsTab: 'Warnings',
  preferencesButtonAriaLabel: 'Preferences',
  paneCloseButtonAriaLabel: 'Close',
  preferencesModalHeader: 'Preferences',
  preferencesModalCancel: 'Cancel',
  preferencesModalConfirm: 'Confirm',
  preferencesModalWrapLines: 'Wrap lines',
  preferencesModalTheme: 'Theme',
  preferencesModalLightThemes: 'Light themes',
  preferencesModalDarkThemes: 'Dark themes',
};

export const SqlEditorForm: React.FC<SqlEditorFormProps> = ({
  sql, onChange, onSchemaUpdate, onRegenerate, datasetId: _datasetId, fileId: _fileId, schema, filePreview,
}) => {
  const [ace, setAce] = useState<any>();
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState<Partial<CodeEditorProps.Preferences>>({ wrapLines: true, theme: 'dawn' });
  const [validationResult, setValidationResult] = useState<{ valid: boolean; message: string } | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [preview, setPreview] = useState<{ headers: string[]; rows: string[][] } | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [editorHeight, setEditorHeight] = useState(240);
  const [aiReview, setAiReview] = useState<string | null>(null);
  const [isReviewing, setIsReviewing] = useState(false);

  // Auto-run preview when SQL or filePreview changes, so schemaConfig reflects the
  // transform output (not the original upload schema). Debounced 500ms to avoid
  // hammering DuckDB on every keystroke.
  React.useEffect(() => {
    if (!sql || !filePreview || filePreview.length < 2) return;
    const timer = setTimeout(() => {
      handlePreview();
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sql, filePreview]);

  React.useEffect(() => {
    async function loadAce() {
      const aceModule = await import('ace-builds');
      await import('ace-builds/src-noconflict/mode-sql');
      await import('ace-builds/src-noconflict/theme-dawn');
      await import('ace-builds/src-noconflict/theme-tomorrow_night_bright');
      await import('ace-builds/src-noconflict/ext-language_tools');
      setAce(aceModule);
      setLoading(false);
    }
    loadAce();
  }, []);

  /** Build rows for DuckDB-WASM from filePreview (row 0 = headers, rest = data) */
  const buildSourceRows = (): Record<string, any>[] => {
    if (!filePreview || filePreview.length < 2) return [];
    const headers = filePreview[0];
    return filePreview.slice(1).map(row => {
      const obj: Record<string, any> = {};
      headers.forEach((h, i) => { obj[h] = row[i] ?? null; });
      return obj;
    });
  };

  const handleValidate = async () => {
    setIsValidating(true);
    if (!sql.trim()) {
      setValidationResult({ valid: false, message: 'SQL query cannot be empty' });
      setIsValidating(false);
      return;
    }
    try {
      // Pass real rows so validation catches "column not found" errors,
      // not just pure syntax errors. Matches the schema Preview will see.
      const rows = buildSourceRows();
      const result = await validateSql(sql, rows);
      if (result.valid) {
        setValidationResult({ valid: true, message: 'SQL syntax is valid' });
      } else {
        const msg = String(result.error || 'Unknown error').split('\n')[0];
        setValidationResult({ valid: false, message: `Syntax error: ${msg}` });
      }
    } catch (e: any) {
      const msg = String(e.message || e).split('\n')[0];
      setValidationResult({ valid: false, message: `Validation failed: ${msg}` });
    }
    setIsValidating(false);
  };

  /** Extract output column names from a SELECT SQL statement */
  const extractColumnsFromSql = (sqlText: string): string[] => {
    const match = sqlText.match(/SELECT\s+([\s\S]+?)\s+FROM\s/i);
    if (!match) return [];
    return match[1].split(',').map(col => {
      const trimmed = col.trim();
      const asMatch = trimmed.match(/\bAS\s+(\w+)\s*$/i);
      if (asMatch) return asMatch[1];
      const parts = trimmed.split(/\s+/);
      return parts[parts.length - 1].replace(/["`[\]]/g, '');
    }).filter(Boolean);
  };

  const handlePreview = async () => {
    setIsPreviewing(true);
    setPreview(null);
    try {
      if (!filePreview || filePreview.length < 2) throw new Error('No file data available');
      const data = buildSourceRows();

      // DuckDB-WASM runs the same SQL engine as the backend Glue Job (DuckDB).
      // No dialect translation needed — what works here works in production.
      const result = await runSql<Record<string, any>>(sql, data);

      if (result.length > 0) {
        const resultHeaders = Object.keys(result[0]);
        setPreview({ headers: resultHeaders, rows: result.slice(0, 100).map(r => resultHeaders.map(h => r[h])) });
        if (onSchemaUpdate) {
          onSchemaUpdate(resultHeaders.map(h => ({
            columnName: h,
            dataType: inferType(result, h),
            nullable: true,
            sampleValues: extractSampleValues(result, h, 5),
          })));
        }
      } else {
        setPreview({ headers: extractColumnsFromSql(sql), rows: [] });
        const cols = extractColumnsFromSql(sql);
        if (onSchemaUpdate && cols.length > 0) {
          onSchemaUpdate(cols.map(c => ({ columnName: c, dataType: 'string', nullable: true, sampleValues: [] })));
        }
      }
      setValidationResult({ valid: true, message: `Preview: ${result.length} rows` });
    } catch (e: any) {
      // Fallback: parse columns from SQL and still update schema on best-effort basis,
      // but surface the preview failure as an error so the user knows preview didn't run.
      const cols = extractColumnsFromSql(sql);
      if (onSchemaUpdate && cols.length > 0) {
        onSchemaUpdate(cols.map(c => ({ columnName: c, dataType: 'string', nullable: true, sampleValues: [] })));
      }
      setValidationResult({ valid: false, message: `Preview failed: ${e.message}` });
    }
    setIsPreviewing(false);
  };

  /** Infer column type from sample data */
  const STRING_COL_RE = /(?:^|[_\s])(comment|remark|booking|reason|note|description|memo|message|text|narrative)(?:$|[_\s]|s)/i;
  const ID_COL_RE = /(?:^|[_\s])(id|code|number|no|num|ref|sku|ean|upc|isbn|asin|arn|uuid|guid)(?:$|[_\s]|s)|#/i;
  const inferType = (rows: Record<string, any>[], col: string): string => {
    if (STRING_COL_RE.test(col)) return 'string';
    if (ID_COL_RE.test(col)) return 'string';
    const sample = rows.slice(0, 20).map(r => r[col]).find(v => v != null);
    if (sample === undefined) return 'string';
    if (typeof sample === 'number') return Number.isInteger(sample) ? 'integer' : 'decimal';
    if (typeof sample === 'boolean') return 'boolean';
    if (/^\d{4}-\d{2}-\d{2}/.test(String(sample))) return 'timestamp';
    return 'string';
  };

  /** Extract up to N non-null distinct sample values for a column, as strings. */
  const extractSampleValues = (rows: Record<string, any>[], col: string, max: number): string[] => {
    const seen = new Set<string>();
    for (const r of rows) {
      const v = r[col];
      if (v === null || v === undefined || v === '') continue;
      const s = String(v);
      if (seen.has(s)) continue;
      seen.add(s);
      if (seen.size >= max) break;
    }
    return Array.from(seen);
  };

  const handleAiReview = async () => {
    if (!sql.trim()) return;
    setIsReviewing(true);
    setAiReview(null);
    try {
      const res = await apiFetch(apiUrl('/api/ai/review-sql'), {
      ...apiFetchDefaults,
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sql, schema }),
      });
      if (!res.ok) throw new Error('AI review failed');
      const data = await res.json();
      setAiReview(data.review || 'No feedback from AI.');
    } catch (e: any) {
      setAiReview(`Error: ${e.message}`);
    }
    setIsReviewing(false);
  };

  return (
    <SpaceBetween size="m">
      <Container
        header={
          <Header
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => onChange(formatSql(sql))} iconName="edit">
                  Format SQL
                </Button>
                <Button onClick={handlePreview} loading={isPreviewing} disabled={!filePreview || filePreview.length < 2}>
                  Preview result{!filePreview ? ' (upload file first)' : ''}
                </Button>
                <Button onClick={handleValidate} loading={isValidating}>
                  Validate SQL
                </Button>
                <Button onClick={handleAiReview} loading={isReviewing} iconName="gen-ai">
                  AI Review
                </Button>
                {onRegenerate && (
                  <Button iconName="refresh" onClick={onRegenerate}>Re-generate</Button>
                )}
              </SpaceBetween>
            }
          >
            SQL Transformation
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Alert type="info" header="Table name must be 'source' or 'source_table'">
            Your SQL can only reference the uploaded file as <code>source</code> or <code>source_table</code>.
            Any other name will fail. Example: <code>SELECT * FROM source WHERE status = 'active'</code>.
            <br />
            Preview uses DuckDB-WASM in your browser — the exact same SQL engine as production.
            What works in preview will work in production, no surprises.
            <br />
            Note: the dataset name you chose in Basic Settings is NOT the table name here. It only affects the final Glue/Andes table naming.
          </Alert>

          <ExpandableSection
            headerText="📖 SQL Syntax Cheatsheet (click to expand)"
            variant="footer"
          >
            <div style={{ fontSize: '13px', lineHeight: '1.7' }}>
              <strong>Quotes (DuckDB / ANSI SQL):</strong>
              <ul style={{ marginTop: 4, marginBottom: 8 }}>
                <li><code>'text'</code> → <strong>string value</strong> (single quotes)</li>
                <li><code>"col"</code> → <strong>column / identifier name</strong> (double quotes, usually optional)</li>
                <li><code>`col`</code> → escape a reserved keyword used as column name</li>
              </ul>

              <strong>Common patterns:</strong>
              <ul style={{ marginTop: 4, marginBottom: 8 }}>
                <li>Constant column: <code>'staruploader' AS system</code> <span style={{ color: '#545b64' }}>(NOT <code>"'staruploader'"</code>)</span></li>
                <li>NULL column: <code>NULL AS optional_col</code></li>
                <li>Type cast: <code>CAST(col AS VARCHAR)</code> or <code>col::VARCHAR</code></li>
                <li>Date literal: <code>DATE '2026-05-03'</code></li>
                <li>Timestamp literal: <code>TIMESTAMP '2026-05-03 10:00:00'</code></li>
                <li>String concat: <code>col1 || '-' || col2</code></li>
                <li>Coalesce: <code>COALESCE(col, 'default')</code></li>
                <li>Rename column: <code>old_name AS new_name</code></li>
                <li>Filter non-null: <code>WHERE col IS NOT NULL</code></li>
                <li>Like (case-insensitive): <code>col ILIKE '%pattern%'</code></li>
              </ul>

              <strong>DuckDB-specific (work in preview + Glue):</strong>
              <ul style={{ marginTop: 4, marginBottom: 0 }}>
                <li>Current date/time: <code>CURRENT_DATE</code>, <code>CURRENT_TIMESTAMP</code></li>
                <li>Date parts: <code>YEAR(col)</code>, <code>MONTH(col)</code>, <code>DATE_TRUNC('month', col)</code></li>
                <li>String functions: <code>UPPER(col)</code>, <code>TRIM(col)</code>, <code>SUBSTR(col, 1, 10)</code></li>
                <li>Conditional: <code>CASE WHEN cond THEN a ELSE b END</code></li>
              </ul>
            </div>
          </ExpandableSection>

          <CodeEditor
            ace={ace}
            value={sql}
            language="sql"
            onDelayedChange={({ detail }) => onChange(detail.value)}
            preferences={preferences}
            onPreferencesChange={({ detail }) => setPreferences(detail)}
            loading={loading}
            editorContentHeight={editorHeight}
            onEditorContentResize={({ detail }) => setEditorHeight(detail.height)}
            themes={{ light: ['dawn', 'chrome', 'github'], dark: ['tomorrow_night_bright', 'monokai'] }}
            i18nStrings={i18nStrings}
            ariaLabel="SQL transformation editor"
          />

          {validationResult && (
            <Alert type={validationResult.valid ? 'success' : 'error'}>
              {validationResult.message}
            </Alert>
          )}

          {aiReview && (
            <Alert type="info" header="AI Review" dismissible onDismiss={() => setAiReview(null)}>
              <div style={{ fontSize: '13px' }}>
                <Markdown remarkPlugins={[remarkGfm]}>{aiReview}</Markdown>
              </div>
            </Alert>
          )}

          {preview && (
            <Table
              columnDefinitions={preview.headers.map(h => ({
                id: h, header: h, cell: (item: Record<string, string>) => item[h] || '',
              }))}
              items={preview.rows.map(row => {
                const obj: Record<string, string> = {};
                preview.headers.forEach((h, i) => { obj[h] = row[i]; });
                return obj;
              })}
              variant="embedded"
              header={<Header>Preview ({preview.rows.length} rows)</Header>}
            />
          )}
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};

export default SqlEditorForm;
